import {
  Button,
  ButtonAnchor,
  ButtonLink,
  IconSet,
} from '@postscript/components';
import CreateFlowModal from 'components/flowBuilder/components/modals/CreateFlowModal';
import { FlowTypes } from 'components/flowBuilder/constants';
import { useUser } from 'controllers/contexts/user';
import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';
import type { WorkflowContent } from 'providers/ProgressiveOnboarding/types';
import { useState } from 'react';
import {
  logButtonClickEvent,
  logLinkClickEvent,
  PRODUCT_AREAS,
} from 'utils/events';
import {
  AUTOMATIONS_HELP_LINK,
  AUTOMATION_ACTIVATED_TOUCHLESS_STEP_KEY,
  BUILD_CAMPAIGN_PATH,
  CAMPAIGN_HELP_LINK,
  CAMPAIGN_SENT_TOUCHLESS_STEP_KEY,
  POPUP_ACTIVATED_TOUCHLESS_STEP_KEY,
  POPUP_CONFIG_BASE_PATH,
  PROGRESSIVE_ONBOARDING_SOURCE,
  PS_APP_PROG_ONBOARDING_FEATURE_KEY,
  SUBSCRIBERS_IMPORTED_TOUCHLESS_STEP_KEY,
  SUBSCRIBER_LIST_PATH,
} from './constants';

export const ACTIVATE_AUTOMATION_TOUCHLESS_STEP_CONTENT: WorkflowContent['steps'] =
  {
    [AUTOMATION_ACTIVATED_TOUCHLESS_STEP_KEY]: {
      key: AUTOMATION_ACTIVATED_TOUCHLESS_STEP_KEY,
      guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
      title: 'Activate Automations',
      content:
        'Set up automated flows to message subscribers at key points in their journey, such as cart additions, first purchases, or SMS signup.',
      icon: 'Disc',
      PrimaryAction: () => {
        const { user } = useUser();
        const { setActiveGuide } = useProgressiveOnboarding();
        const [createFlowModalIsOpen, setCreateFlowModalIsOpen] =
          useState(false);

        return (
          <>
            <Button
              onClick={(event) => {
                logButtonClickEvent(event, {
                  productArea: PRODUCT_AREAS.AUTOMATIONS,
                  source: PROGRESSIVE_ONBOARDING_SOURCE,
                  workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                  step: AUTOMATION_ACTIVATED_TOUCHLESS_STEP_KEY,
                  shop_id: user.shop_id,
                });
                setCreateFlowModalIsOpen(true);
              }}
            >
              Build an Automation
            </Button>
            <CreateFlowModal
              flowType={FlowTypes.AUTOMATION}
              isOpen={createFlowModalIsOpen}
              onCancel={() => {
                setCreateFlowModalIsOpen(false);
                setActiveGuide(undefined);
              }}
            />
          </>
        );
      },
      SecondaryAction: () => {
        const { user } = useUser();

        return (
          <ButtonAnchor
            href={AUTOMATIONS_HELP_LINK}
            variant="plain"
            icon={IconSet.Share}
            iconPosition="right"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              logLinkClickEvent(event, {
                productArea: PRODUCT_AREAS.AUTOMATIONS,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                step: AUTOMATION_ACTIVATED_TOUCHLESS_STEP_KEY,
                shop_id: user.shop_id,
              });
            }}
          >
            More Help
          </ButtonAnchor>
        );
      },
    },
  };

export const CAMPAIGN_SENT_TOUCHLESS_STEP_CONTENT: WorkflowContent['steps'] = {
  [CAMPAIGN_SENT_TOUCHLESS_STEP_KEY]: {
    key: CAMPAIGN_SENT_TOUCHLESS_STEP_KEY,
    guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
    title: 'Send your First Campaign',
    content: (
      <>
        Use campaigns to schedule and send messages to a specific customer
        segment or your entire customer base.
        <br />
        <br />
        Campaigns are ideal for new product launches or announcements such as a{' '}
        <b>New Number Campaign.</b>
      </>
    ),
    icon: 'Disc',
    PrimaryAction: () => {
      const { user } = useUser();

      return (
        <ButtonLink
          to={BUILD_CAMPAIGN_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.CAMPAIGNS,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: CAMPAIGN_SENT_TOUCHLESS_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Build a Campaign
        </ButtonLink>
      );
    },
    SecondaryAction: () => {
      const { user } = useUser();

      return (
        <ButtonAnchor
          href={CAMPAIGN_HELP_LINK}
          variant="plain"
          icon={IconSet.Share}
          iconPosition="right"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(event) => {
            logLinkClickEvent(event, {
              productArea: PRODUCT_AREAS.CAMPAIGNS,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: CAMPAIGN_SENT_TOUCHLESS_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          More Help
        </ButtonAnchor>
      );
    },
  },
};

export const ACTIVATE_POPUP_TOUCHLESS_STEP_CONTENT: WorkflowContent['steps'] = {
  [POPUP_ACTIVATED_TOUCHLESS_STEP_KEY]: {
    key: POPUP_ACTIVATED_TOUCHLESS_STEP_KEY,
    guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
    title: 'Build Your Popups',
    content:
      "Postscript's new popup editor grows your list and drives revenue with built-in compliance, flexible segmenting, and Onsite Opt-in (delivers up to 3x more subscribers).",
    icon: 'Disc',
    PrimaryAction: () => {
      const { user } = useUser();

      return (
        <ButtonLink
          to={POPUP_CONFIG_BASE_PATH}
          onClick={(event) => {
            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.POPUPS,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: POPUP_ACTIVATED_TOUCHLESS_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Create New Popup
        </ButtonLink>
      );
    },
    SecondaryAction: ({ step }) => {
      const { markStepCompleted } = useProgressiveOnboarding();
      const { user } = useUser();

      if (step.state === 'complete') {
        return null;
      }

      return (
        <Button
          variant="text"
          onClick={(event) => {
            markStepCompleted({
              guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              stepKey: POPUP_ACTIVATED_TOUCHLESS_STEP_KEY,
            });

            logButtonClickEvent(event, {
              productArea: PRODUCT_AREAS.POPUPS,
              source: PROGRESSIVE_ONBOARDING_SOURCE,
              workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
              step: POPUP_ACTIVATED_TOUCHLESS_STEP_KEY,
              shop_id: user.shop_id,
            });
          }}
        >
          Mark as Complete
        </Button>
      );
    },
  },
};

export const IMPORT_SUBSCRIBERS_TOUCHLESS_STEP_CONTENT: WorkflowContent['steps'] =
  {
    [SUBSCRIBERS_IMPORTED_TOUCHLESS_STEP_KEY]: {
      key: SUBSCRIBERS_IMPORTED_TOUCHLESS_STEP_KEY,
      guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
      title: 'Import Subscribers',
      content: (
        <>
          Migrate your existing subscriber list to Postscript.
          <br />
          <br />
          1. Disable all subscriber collection tools and messaging on your
          previous platform.
          <br />
          <br />
          2. Complete the &quot;Create Upload Request&quot; form and upload your
          CSV file.
          <br />
          <br />
          3. Review the subscriber counts to confirm they are accurate.
          <br />
          <br />
          4. After submission, wait for approval (typically 1–3 business days).
        </>
      ),
      icon: 'Disc',
      PrimaryAction: () => {
        const { user } = useUser();

        return (
          <ButtonLink
            to={SUBSCRIBER_LIST_PATH}
            onClick={(event) => {
              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.SUBSCRIBERS,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                step: SUBSCRIBERS_IMPORTED_TOUCHLESS_STEP_KEY,
                shop_id: user.shop_id,
              });
            }}
          >
            Go to Subscriber List
          </ButtonLink>
        );
      },
      SecondaryAction: ({ step }) => {
        const { markStepCompleted } = useProgressiveOnboarding();
        const { user } = useUser();

        if (step.state === 'complete') {
          return null;
        }

        return (
          <Button
            variant="text"
            onClick={(event) => {
              markStepCompleted({
                guideKey: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                stepKey: SUBSCRIBERS_IMPORTED_TOUCHLESS_STEP_KEY,
              });

              logButtonClickEvent(event, {
                productArea: PRODUCT_AREAS.SUBSCRIBERS,
                source: PROGRESSIVE_ONBOARDING_SOURCE,
                workflow: PS_APP_PROG_ONBOARDING_FEATURE_KEY,
                step: SUBSCRIBERS_IMPORTED_TOUCHLESS_STEP_KEY,
                shop_id: user.shop_id,
              });
            }}
          >
            Mark as Complete
          </Button>
        );
      },
      badge: {
        text: 'For Migration Only',
        variant: 'info',
      },
    },
  };
